// src/features/records/services/interactions/comments.js

import { auth, db, functions } from '../../../../firebase';
import { collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { requireUser } from '../utils/auth';

const deleteRecordCommentFn = httpsCallable(functions, 'deleteRecordComment');

/**
 * Add a root comment to a record.
 */
export async function addRootComment(recordId, text) {
  requireUser();

  if (!recordId || typeof recordId !== 'string') {
    throw new Error('recordId is required');
  }

  const trimmedText = typeof text === 'string' ? text.trim() : '';
  if (!trimmedText) {
    throw new Error('Comment text is required');
  }
  if (trimmedText.length > 5000) {
    throw new Error('Comment text exceeds 5000 characters');
  }

  try {
    const commentRef = doc(collection(db, 'records', recordId, 'comments'));
    const comment = {
      authorId: auth.currentUser.uid,
      text: trimmedText,
      parentId: null,
      threadRootId: commentRef.id,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deletedAt: null,
      deletedBy: null
    };

    await setDoc(commentRef, comment);

    return {
      ...comment,
      id: commentRef.id,
      createdAt: new Date()
    };
  } catch (error) {
    console.error('Error adding root comment:', error);
    throw error;
  }
}

/**
 * Add a reply to a root comment.
 */
export async function addReply(recordId, rootCommentId, text) {
  requireUser();

  if (!recordId || typeof recordId !== 'string') {
    throw new Error('recordId is required');
  }

  if (!rootCommentId || typeof rootCommentId !== 'string') {
    throw new Error('rootCommentId is required');
  }

  const trimmedText = typeof text === 'string' ? text.trim() : '';
  if (!trimmedText) {
    throw new Error('Reply text is required');
  }
  if (trimmedText.length > 5000) {
    throw new Error('Reply text exceeds 5000 characters');
  }

  try {
    const commentRef = doc(collection(db, 'records', recordId, 'comments'));
    const reply = {
      authorId: auth.currentUser.uid,
      text: trimmedText,
      parentId: rootCommentId,
      threadRootId: rootCommentId,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      deletedAt: null,
      deletedBy: null
    };

    await setDoc(commentRef, reply);

    return {
      ...reply,
      id: commentRef.id,
      createdAt: new Date()
    };
  } catch (error) {
    console.error('Error adding reply:', error);
    throw error;
  }
}

/**
 * Delete (tombstone) a comment.
 */
export async function deleteComment(recordId, commentId) {
  requireUser();

  if (!recordId || typeof recordId !== 'string') {
    throw new Error('recordId is required');
  }

  if (!commentId || typeof commentId !== 'string') {
    throw new Error('commentId is required');
  }

  try {
    const result = await deleteRecordCommentFn({
      recordId,
      commentId
    });
    return result?.data ?? null;
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
}
